import { useValidateOtpMutation, useSendOtpMutation } from 'lib/services/otp';
import { Otp } from '../presentational/Otp';
import { useForm } from 'lib/utils';
import { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import useCountdownTimer from 'lib/hooks/useCountdownTimer';
import { AUTH_TELL_GATEWAY } from '../../../../constants/shared/routes';
import { AuthStep } from 'constants/shared/enums';
import { setAuthStep } from 'utils/generic';
import jwt_decode from 'jwt-decode';
import { finalizeAuthAndSetToken } from 'utils/auth';
import ResetButton from 'components/shared/layout/resetButton/ResetButton';
import { useDispatch } from 'react-redux';
import { TokenData } from 'lib/type/types';
import { identifyCustomerIO } from 'utils/customerIO';
import { showModal } from 'actions/modal';
import { ACCOUNT_BLOCKED_MODAL } from 'constants/shared/modalTypes';
import useTranslate from 'lib/languages/useTranslate';
import { logout } from 'lib/actions';

interface Error {
    status?: number;
    data?: string;
}

export const OtpContainer = () => {
    const translate = useTranslate('accountClosed');
    const history = useHistory();
    const authStep = localStorage.getItem('authStep') as AuthStep;
    const cognitoToken = localStorage.getItem('cognitoToken');
    const consentId = sessionStorage.getItem('consentId');

    const [form, handleChange] = useForm({ otp: '' });

    const [sendOtp, sendOtpResult] = useSendOtpMutation();
    const [validateOtp, validateOtpResult] = useValidateOtpMutation();
    const dispatch = useDispatch();
    const {
        remainingSeconds,
        startCountdown,
        isCountdownActive,
    } = useCountdownTimer(30);
    const isLoading = sendOtpResult.isLoading || validateOtpResult.isLoading;

    const rawError = (validateOtpResult.error ||
        sendOtpResult.error) as Error | null;

    const extractErrorMessage = error => {
        if (!error) return null;
        if (error?.status === undefined) {
            return 'Something went wrong. Please try again.';
        }
        return error?.data;
    };

    const onCloseModal = useCallback(async () => {
        await dispatch(logout());
        setAuthStep(null);
    }, [dispatch]);

    useEffect(() => {
        if (authStep === AuthStep.OTP) {
            sendOtp();
        }
    }, [sendOtp, authStep]);

    useEffect(() => {
        if (!cognitoToken) return;

        const { usernameV1, accountIdV2 } = jwt_decode<TokenData>(cognitoToken);

        if (!accountIdV2) {
            dispatch(
                showModal({
                    type: ACCOUNT_BLOCKED_MODAL,
                    props: {
                        closeModal: onCloseModal,
                        titleText: translate('titleText'),
                        descriptionText: translate('descriptionText'),
                        confirmText: translate('confirm'),
                    },
                }),
            );
        }

        if (!validateOtpResult.isSuccess) return;

        identifyCustomerIO(usernameV1);

        if (consentId) {
            setAuthStep(AuthStep.TELL_GATEWAY);
            history.push(AUTH_TELL_GATEWAY);
        } else {
            finalizeAuthAndSetToken(cognitoToken);
            history.push('/');
        }
    }, [
        validateOtpResult.isSuccess,
        history,
        dispatch,
        cognitoToken,
        consentId,
        translate,
        onCloseModal,
    ]);

    return (
        <>
            <Otp
                otp={form.otp}
                handleChange={handleChange}
                handleSubmit={handleSubmit}
                handleResendOTP={handleResendOTP}
                isPostingOTP={isLoading}
                error={extractErrorMessage(rawError)}
                remainingSeconds={remainingSeconds}
                isCountdownActive={isCountdownActive}
            />
            <ResetButton />
        </>
    );

    async function handleSubmit() {
        const otp = form.otp;
        validateOtp({ otp });
    }

    function handleResendOTP() {
        if (!isCountdownActive) {
            sendOtp();
            startCountdown();
        }
    }
};
